import debounce from 'just-debounce';

import MessageBanner from './MessageBanner';
import Modal from './Modal';

import {
  CheckoutValidator,
  Images,
  getHeaderOffset,
  cdfQuantityTotal
} from '#helpers';

class AddToCartFlyout {
  constructor() {
    this.formData = {};
    this.settings = {};
    this.events = [];
    this.Images = new Images();
    this.flyOutSelector = '[data-atc-banner]';
    this.$atcTemplate = $(`[data-templates] ${this.flyOutSelector}`);

    this._onInit = this._onInit.bind(this);
    this._onOpen = this._onOpen.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onError = this._onError.bind(this);
    this._onSuccess = this._onSuccess.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);

    this.messageBanner = new MessageBanner();

    this.header = document.querySelector('.site-header-wrapper');
    this.$header = $(this.header);
    this.$body = $(document.body);

    this.siteData = this._loadData();
    if (this.siteData && 'restricted_amount' in this.siteData) {
      this.restrictionAmountMax = this.siteData.restriction_amount_max;
      this.restrictedAmount = this.siteData.restricted_amount;
    }

    this.$body.on(
      'update-restricted-amount',
      this._updateRestrictedAmount.bind(this)
    );
  }

  async init(formData, options, callbacks = {}) {
    const $atcButton = options.$atcButton;
    this.availabilities = options.availabilities;

    this._disableButton($atcButton);

    // If any FlyOuts are fully open, close them
    await this._closeFlyOuts();

    this.formData = formData;

    // Allows ATC Flow to be overridden
    this.callbacks = $.extend(
      {
        onInit: this._onInit,
        onClose: this._onClose,
        onError: this._onError,
        onOpen: this._onOpen,
        onSuccess: this._onSuccess
      },
      callbacks
    );

    this.settings = $.extend(
      {
        moneyFormat: null
      },
      options.settings
    );

    this.callbacks.onInit();

    this._updateCart($atcButton);
  }

  unload() {
    this.messageBanner.dismissBanners();
    this.checkoutValidator.unload();
    this._removeFlyOuts();
    delete this.Images;
    this.$body.off('update-restricted-amount');
  }

  _loadData() {
    const dataEl = document.body.querySelector('[data-site-data]');
    if (!dataEl) {
      return {};
    }

    // Load data from attribute, or innerHTML
    const data = dataEl.getAttribute('data-site-data') || dataEl.innerHTML;

    try {
      return JSON.parse(data);
    } catch (error) {
      console.warn(
        `AddToCartFlyout: invalid site data found. ${error.message}`
      );
      return {};
    }
  }

  _getFormDataPoint(formFieldName, isInteger = false) {
    const formField = this.formData.filter(data => {
      return data.name === formFieldName;
    });

    let formFieldValue = false;

    if (formField.length) {
      formFieldValue = formField[0].value;
      formFieldValue = isInteger
        ? parseInt(formFieldValue, 10)
        : formFieldValue;
    }

    return formFieldValue;
  }

  /* eslint-disable no-shadow */
  _updateCart($atcButton) {
    const $flyOut = this.$atcTemplate.clone();
    const variantId = this._getFormDataPoint('id');
    const quantity = this._getFormDataPoint('quantity', true);
    const sku = this._getFormDataPoint('sku');
    const cdf = this._getFormDataPoint('cdf');
    const consumerUnits = this._getFormDataPoint('consumer_units', true);
    const maxQuantity = parseInt(this.availabilities[variantId].vq, 10);

    const data = {
      action: 'add',
      id: variantId,
      maxQuantity,
      quantity,
      originallyRequestedQuantity: quantity
    };

    const itemRequiresDespoit = cdf && consumerUnits && sku;

    if (itemRequiresDespoit) {
      data.cdf = cdf;
      data.consumerUnits = consumerUnits;
      data.sku = sku;
    }

    const $elements = {
      $flyOut,
      $atcButton
    };

    const callbacks = {
      doneCallback: this._onCartUpdateDone.bind(this),
      failCallback: this._onCartUpdateFail.bind(this)
    };

    this.$body.trigger('updateCartQuantities', { data, callbacks, $elements });
  }

  // NOTE: If on cart page refresh the page without adding the flyout
  _onCartUpdateDone(response, request, $elements) {
    if ($(document.body).hasClass('template-cart')) {
      window.location.reload();
    } else {
      this._displayFlyout(response, request, $elements);
    }
  }

  _onCartUpdateFail(response, request, $elements) {
    console.log('ERROR', response);
    const { responseText } = response;
    const text =
      typeof responseText === 'string'
        ? JSON.parse(responseText)
        : responseText;
    const error = text.description;

    this._enableButton($elements.$atcButton);
    this.callbacks.onError(error);
  }
  /* eslint-enable no-shadow */

  _displayFlyout(response, request, $elements) {
    const { $flyOut, $atcButton } = $elements;
    const updatedItems = response.items.filter(item => {
      return item.id === parseInt(request.id, 10);
    });
    const updatedItem = updatedItems[0];

    if (updatedItem.featured_image.url) {
      const imageUrl = this.Images.getSizedImageUrl(
        updatedItem.featured_image.url,
        '200x'
      );
      const image = this.Images.loadImage(imageUrl);

      $flyOut.find('[data-atc-banner-product-image]').html(`
          <img src="${image}" alt="${updatedItem.featured_product_title}">
        `);
    }

    let productTitle = $atcButton
      .closest('[data-product-main-sticky]')
      .find('[data-product-title]')
      .text();

    if (productTitle === '') {
      productTitle = updatedItem.product_title;
    }

    $flyOut.find('[data-atc-banner-product-title]').text(`${productTitle}`);

    if (updatedItem.properties && updatedItem.properties.gram_equivalency) {
      // Update restriction message
      this.restrictedAmount =
        this.restrictedAmount +
        updatedItem.properties.gram_equivalency *
          request.originallyRequestedQuantity;

      $flyOut.find('[data-atc-banner-restriction]').val(this.restrictedAmount);
    }

    this.checkoutValidator = new CheckoutValidator(
      $flyOut,
      $flyOut.find('form')
    );

    if (this.restrictedAmount > this.restrictionAmountMax) {
      $flyOut.find('[data-atc-banner-message]').addClass('visible');
      $flyOut
        .find('[data-atc-banner-checkout-button]')
        .attr('disabled', true)
        .addClass('disabled');
    }

    if (
      updatedItem.variant_options[0] !== 'Title' &&
      updatedItem.variant_options[0] !== 'Default Title'
    ) {
      $flyOut
        .find('[data-atc-banner-product-options]')
        .text(updatedItem.variant_options.join(', '));
    }

    /*
    TODO: Bring in variant, and use that to check compare_at_price
      to see if the item is on sale
    */
    const $productPrice = $flyOut.find('[data-atc-banner-product-price]');
    $productPrice.text(
      `${request.originallyRequestedQuantity} x ` +
        Shopify.formatMoney(updatedItem.price, this.settings.moneyFormat)
    );

    // AFTER Old cart get request
    this.callbacks.onSuccess();

    // Reset formData in case instance is never cleared
    this.formData = {};

    const $subTotal = $flyOut.find('[data-atc-banner-cart-subtotal]');
    $subTotal.text(
      Shopify.formatMoney(
        response.original_total_price,
        this.settings.moneyFormat
      )
    );

    const cdfCount = cdfQuantityTotal(response);
    const cartTotal = response.item_count - cdfCount;

    $flyOut.find('[data-atc-banner-cart-button]').find('span').text(cartTotal);

    this.$body.append($flyOut);

    // Notifiy Header of new cart count
    this.$header.trigger('cartcount:update', { response });

    this._positionFlyOut($flyOut);
    this._bindEvents($flyOut);
    this._openFlyOut($flyOut, $atcButton);
  }

  _onError(error) {
    // Display custom message to hide display of variant quantity in Shopify error message
    const errorMessage = this.siteData.context.atc.quantity_unavailable;
    this.messageBanner.message(errorMessage, 'error');
  }

  _onInit() {
    this.messageBanner.dismissBanners();
  }

  _onOpen() {
    /*
      The `this.callbacks.onOpen` is used pull keyboard focus into the active flyout
     */
    console.warn('AddToCartFlyout onOpen callback not specified');
  }

  _onClose() {
    /*
      The `this.callbacks.onClose` is used to return focus to originating DOM
     */

    console.warn('AddToCartFlyout onClose callback not specified');
  }

  _onSuccess() {
    /*
      By default, the ATC Flyout doesn't need any additional success callbacks

      The `this.callbacks.onSuccess` is used to allow other views to initiate
      behaviour when a product has been added to the cart
     */
  }

  _bindEvents($flyOut) {
    this.events.push([
      $flyOut.on('click.atc-banner', '[data-atc-banner-close]', event => {
        event.preventDefault();
        this._closeFlyOut($flyOut);
      }),
      $flyOut.on(
        'click.atc-banner',
        '[data-atc-banner-checkout-button]',
        event => {
          event.preventDefault();
          window.location = '/checkout';
        }
      ),
      $flyOut.on('click.atc-banner', '[data-privacy-modal-trigger]', event => {
        event.preventDefault();
        const modal = new Modal();
        modal.open('[data-privacy-modal]', 'privacy-consent');
      }),
      this.$body.on('position-banners.atc-banner', () => {
        this._positionFlyOut($flyOut);
      }),
      $(window).on('resize.atc-banner', () => {
        debounce(this._positionFlyOut($flyOut), 250);
      }),
      $(window).on('scroll.atc-banner', () => {
        this._positionFlyOut($flyOut);
      }),
      $(document).on(
        'touchstart.atc-banner, click.atc-banner',
        this._handleDocumentClick
      )
    ]);
  }

  _updateRestrictedAmount(restrictedAmount) {
    this.restrictedAmount = restrictedAmount;
  }

  /**
   * Trigger any existing open FlyOuts to close
   *
   * @private
   */
  async _closeFlyOuts($excludeFlyOut = null) {
    const $flyOut = $(`${this.flyOutSelector}[data-flyout-active]`);

    if (!$flyOut.length || ($excludeFlyOut && $flyOut.is($excludeFlyOut))) {
      return;
    }

    return await this._closeFlyOut($flyOut);
  }

  _openFlyOut($flyOut, $atcButton) {
    $flyOut.addClass('animating animating-in').one('trend', () => {
      $flyOut
        .removeClass('animating animating-in')
        .addClass('visible')
        .off('trend');

      $flyOut.attr('data-flyout-active', true);
      this.callbacks.onOpen({
        $container: $flyOut,
        $elementToFocus: $flyOut.find('[data-atc-banner-cart-button]')
      });

      this._enableButton($atcButton);
    });
  }

  /**
   * Close an open FlyOut
   *
   * @param $flyOut
   * @param index
   * @private
   */
  _closeFlyOut($flyOut = null, index = 0) {
    if (!$flyOut) {
      return;
    }

    // Returning a promise to ensure we don't open a flyout before first
    // closing the previously opened flyout
    return new Promise(resolve => {
      $flyOut.addClass('animating animating-out').one('trend', () => {
        $flyOut
          .removeClass('animating animating-out visible')
          .one('trend', () => {
            $flyOut.off('trend');
            this.checkoutValidator.unload();
            this._removeFlyOut($flyOut, index);
            this.callbacks.onClose();
            resolve();
          });
      });
    });
  }

  _removeFlyOuts() {
    $(this.flyOutSelector)
      .filter('[data-flyout-active]')
      .each((index, el) => {
        this._removeFlyOut($(el), index);
      });
  }

  /**
   * Remove the Flyout from the DOM, and clean up listeners
   *
   * @param $flyOut
   * @param index
   * @private
   */
  _removeFlyOut($flyOut, index) {
    if (this.events[index]) {
      this.events[index].forEach($el => $el.off('.atc-banner'));
      this.events.splice(index, 1);
    }

    if ($flyOut) {
      $flyOut.remove();
    }
  }

  /**
   * Position FlyOut beneath header if its in viewport, or at top of viewport
   *
   * @param $flyOut
   * @private
   */
  _positionFlyOut($flyOut) {
    $flyOut.css({ top: getHeaderOffset(this.header, true) });
  }

  _disableButton($atcButton) {
    $atcButton.addClass('processing').prop('disabled', true);
  }

  _enableButton($atcButton) {
    $atcButton.prop('disabled', false).removeClass('processing');
  }

  _handleDocumentClick(event) {
    const target = event.target;
    const $parent = $(target).parents(
      '[data-atc-banner], [data-modal-container], [data-product-atc]'
    );

    if (
      $parent.length ||
      $(target).is('[data-modal-container]') ||
      $(target).is('[data-product-atc]')
    ) {
      return;
    }

    this._closeFlyOuts();
  }
}

export default new AddToCartFlyout();
